/**
 * Tarjeta contenedora de una gráfica de estadísticas (presentacional).
 *
 * Encapsula el patrón de estado de una métrica (cargando / error / datos) para no
 * repetirlo en cada gráfica, contemplando SIEMPRE los tres estados (no pantalla en
 * blanco, `docs/frontend_conventions.md`, Datos). Compone la `Card` de la librería
 * de UI: cabecera con título y un control opcional (las pestañas de período) y el
 * cuerpo con el contenido o el mensaje de estado. Sin lógica de datos: recibe el
 * estado de la query y una función que pinta el contenido con los datos cargados.
 */
import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";

import { statsErrorMessage } from "../format";

interface ChartCardProps<TData> {
  /** Título de la gráfica (p. ej. "Ingresos"). */
  title: string;
  /** Control opcional en la cabecera (p. ej. las pestañas de período). */
  action?: ReactNode;
  /** Estado de la query: cargando. */
  isPending: boolean;
  /** Estado de la query: error. */
  isError: boolean;
  /** Error de la query (para el mensaje). */
  error: unknown;
  /** Datos de la query cuando hubo éxito (`undefined` mientras carga/falla). */
  data: TData | undefined;
  /** Render del contenido con los datos cargados. */
  children: (data: TData) => ReactNode;
}

export function ChartCard<TData>({
  title,
  action,
  isPending,
  isError,
  error,
  data,
  children,
}: ChartCardProps<TData>) {
  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">{title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent>
        {isPending ? (
          <p className="py-6 text-center text-sm text-slate-500" role="status">
            Cargando…
          </p>
        ) : isError || data === undefined ? (
          <p className="py-6 text-center text-sm text-red-600" role="alert">
            {statsErrorMessage(error)}
          </p>
        ) : (
          children(data)
        )}
      </CardContent>
    </Card>
  );
}
